import { useState, useRef, useCallback } from 'react';
import type { AriaAttributes } from 'react';
import { Button as HeadlessButton } from '@headlessui/react';
import { Tooltip, type TooltipSide } from './Tooltip';

export type ButtonVariant = 'primary' | 'secondary' | 'warning' | 'danger' | 'ghost' | 'overlay' | 'ai';
export type ButtonSize = 'xs' | 'compact' | 'small' | 'medium' | 'large';
export type IconPosition = 'left' | 'right' | 'icon-only';

export type ButtonProps = {
  children?: React.ReactNode;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Runs on mousedown before the browser's default (focus change). Used to
   * suppress the default so a button click cannot blur an in-edit control
   * (e.g. a Cancel button next to an inline editor: without suppression the
   * blur would save the edit before the cancel click is processed).
   */
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: IconPosition;
  type?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
  className?: string;
  rounded?: 'full' | 'lg' | 'md' | 'sm' | 'none' | 'xl' | '2xl';
  tooltipSide?: TooltipSide;
  /** Keyboard shortcut shown beside the icon-only button label in its tooltip. */
  tooltipShortcut?: string;
  /** Suppress the auto-tooltip that icon-only buttons get from `ariaLabel`. Use when an outer `<Tooltip>` already provides the hover label. */
  disableTooltip?: boolean;
  /** Forward `aria-expanded` for disclosure / collapsible-trigger buttons. */
  ariaExpanded?: boolean;
  /** Forward the active-page marker for navigation and tab buttons. */
  ariaCurrent?: AriaAttributes['aria-current'];
  /** Forward `aria-controls` for disclosure / collapsible-trigger buttons. */
  ariaControls?: string;
  /** Forward the pressed state for toggle and segmented controls. */
  ariaPressed?: boolean;
  /** Keep toggle-style controls visually fixed while retaining the click ripple. */
  disablePressScale?: boolean;
  /** Keep a button visually static by disabling mouse-following and click effects. */
  disableHoverEffects?: boolean;
};

const sizeClasses = {
  xs: 'h-6 px-2 text-xs',
  compact: 'h-7 px-2 text-xs',
  small: 'h-8 px-3 text-xs',
  medium: 'h-10 px-5 text-sm',
  large: 'h-12 px-6 text-base',
};

const iconOnlyWidthClasses = {
  xs: 'w-6',
  compact: 'w-7',
  small: 'w-8',
  medium: 'w-10',
  large: 'w-12',
};

const variantClasses = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  warning: 'btn-warning',
  danger: 'btn-danger',
  ghost: 'bg-transparent hover:bg-white/5 text-white/70 hover:text-white transition-colors',
  overlay: 'btn-overlay',
  ai: 'btn-ai',
};

export const Button = ({
  children,
  onClick,
  onMouseDown,
  variant = 'primary',
  size = 'small',
  disabled = false,
  icon,
  iconPosition = 'left',
  type = 'button',
  ariaLabel,
  className = '',
  rounded = 'lg',
  tooltipSide,
  tooltipShortcut,
  disableTooltip = false,
  ariaExpanded,
  ariaCurrent,
  ariaControls,
  ariaPressed,
  disablePressScale = false,
  disableHoverEffects = false,
}: ButtonProps) => {
  const isIconOnly = iconPosition === 'icon-only';
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [clickPosition, setClickPosition] = useState({ x: 50, y: 50 });
  const [isClicking, setIsClicking] = useState(false);
  const rafRef = useRef<number | null>(null);
  const pendingMouseRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    pendingMouseRef.current = { x, y };
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const pending = pendingMouseRef.current;
      if (pending) setMousePosition(pending);
    });
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    // Capture click position for ripple effect
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setClickPosition({ x, y });
    }

    // Trigger animation
    setIsClicking(true);
    setTimeout(() => setIsClicking(false), 500);

    // Call original onClick with event
    onClick?.(e);
  };

  const showTooltip = isIconOnly && !!ariaLabel && !disableTooltip;

  const button = (
    <HeadlessButton
      ref={buttonRef}
      type={type}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      disabled={disabled}
      onMouseMove={disableHoverEffects ? undefined : handleMouseMove}
      aria-label={ariaLabel || (isIconOnly && typeof children === 'string' ? children : undefined)}
      aria-expanded={ariaExpanded}
      aria-current={ariaCurrent}
      aria-controls={ariaControls}
      aria-pressed={ariaPressed}
      className={`
        group/btn relative overflow-hidden
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${isIconOnly ? `${iconOnlyWidthClasses[size]} px-0` : ''}
        inline-flex items-center justify-center gap-2
        rounded-${rounded} font-light
        focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
        disabled:opacity-50 disabled:cursor-not-allowed
        motion-reduce:transition-none
        ${className}
      `}
      style={{
        transform: disableHoverEffects ? 'none' : isClicking && !disablePressScale ? 'scale(0.95)' : 'scale(1)',
        transition: disableHoverEffects
          ? 'none'
          : isClicking && !disablePressScale
            ? 'transform 80ms cubic-bezier(0.4, 0, 0.6, 1)'
            : 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    >
      {/* Smooth Glass Ripple Effect */}
      {!disableHoverEffects && isClicking && (
        <span
          className={`absolute inset-0 pointer-events-none rounded-[inherit] overflow-hidden`}
          style={{
            background: `radial-gradient(circle at ${clickPosition.x}% ${clickPosition.y}%, ${
              variant === 'danger'
                ? 'rgba(248, 113, 113, 0.5)'
                : variant === 'secondary'
                  ? 'rgba(103, 232, 249, 0.45)'
                  : variant === 'ai'
                    ? 'rgba(167, 139, 250, 0.45)'
                    : (variant === 'ghost' || variant === 'overlay')
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(255, 255, 255, 0.4)'
            } 0%, ${
              variant === 'danger'
                ? 'rgba(248, 113, 113, 0.15)'
                : variant === 'secondary'
                  ? 'rgba(103, 232, 249, 0.1)'
                  : variant === 'ai'
                    ? 'rgba(167, 139, 250, 0.1)'
                    : (variant === 'ghost' || variant === 'overlay')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.15)'
            } 30%, transparent 70%)`,
            animation: 'smooth-ripple 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            willChange: 'transform, opacity',
          }}
        />
      )}

      {/* Mouse-following glow effect (rAF-throttled) */}
      {!disableHoverEffects && !disabled && (
        <>
          <span
            className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 pointer-events-none rounded-[inherit]"
            style={{
              background: `radial-gradient(circle 120px at ${mousePosition.x}% ${mousePosition.y}%, ${
                variant === 'danger'
                  ? 'rgb(239 68 68 / 0.08)'
                  : variant === 'secondary'
                    ? 'rgb(34 211 238 / 0.1)'
                    : variant === 'ai'
                      ? 'rgb(139 92 246 / 0.12)'
                      : (variant === 'ghost' || variant === 'overlay')
                        ? 'rgba(255, 255, 255, 0.03)'
                        : 'rgba(255, 255, 255, 0.06)'
              }, transparent 80%)`,
            }}
          />
          <span
            className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500 pointer-events-none rounded-[inherit]"
            style={{
              background: `linear-gradient(115deg, transparent ${mousePosition.x - 20}%, ${
                variant === 'danger'
                  ? 'rgb(239 68 68 / 0.05)'
                  : variant === 'secondary'
                    ? 'rgb(34 211 238 / 0.08)'
                    : variant === 'ai'
                      ? 'rgb(139 92 246 / 0.1)'
                      : (variant === 'ghost' || variant === 'overlay')
                        ? 'rgba(255, 255, 255, 0.03)'
                        : 'rgba(255, 255, 255, 0.05)'
              } ${mousePosition.x}%, transparent ${mousePosition.x + 20}%)`,
            }}
          />
          <span
            className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-700 pointer-events-none rounded-[inherit]"
            style={{
              background: `radial-gradient(ellipse 200px 80px at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.04), transparent 60%)`,
            }}
          />
        </>
      )}
      {icon && (iconPosition === 'left' || iconPosition === 'icon-only') && (
        <span className="inline-flex items-center [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
      )}
      {!isIconOnly && children}
      {icon && iconPosition === 'right' && <span className="inline-flex items-center [&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
    </HeadlessButton>
  );

  const tooltipContent = tooltipShortcut ? (
    <>
      <span className="font-medium">{ariaLabel}</span>
      <span className="ml-2 text-xs text-white/50">{tooltipShortcut}</span>
    </>
  ) : ariaLabel;

  return showTooltip ? <Tooltip content={tooltipContent} side={tooltipSide}>{button}</Tooltip> : button;
};
